import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Loader2, Clock, Wallet, Tag, Percent, AlertTriangle, FileDown, FileText, TrendingDown, TrendingUp, StickyNote, Gauge } from 'lucide-react';
import { Button } from '../ui/button';
import { saveAs } from 'file-saver';
import { toast } from 'sonner';
import { getCurrencySymbol } from '../../lib/currency';
import { BenchmarkTrendPanel } from './BenchmarkTrendPanel';
import { RoleMarginMatrix } from '../margin/RoleMarginMatrix';
import { ProductSubscriptionBlock } from './ProductSubscriptionBlock';
import { CommercialAnalysisBlock } from './CommercialAnalysisBlock';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const money = (n, sym) => (n == null ? '—' : `${sym}${Math.round(n).toLocaleString()}`);
const hrs = (n) => (n == null ? '—' : `${Number(n).toLocaleString(undefined, { maximumFractionDigits: 1 })}h`);

const Kpi = ({ icon: Icon, label, value, sub, tone = 'text-[#0F2040]', testId }) => (
  <div className="rounded-lg border border-slate-200 bg-white px-3 py-2.5" data-testid={testId}>
    <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-slate-400"><Icon className="w-3 h-3" />{label}</div>
    <div className={`text-xl font-bold tabular-nums ${tone}`}>{value}</div>
    {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
  </div>
);

/** Leadership/Sales drilldown: effort · resource cost · price · margin, broken down per project factor. */
export function DealSummaryDialog({ estimateId, open, onClose }) {
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState(null);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [ccyMode, setCcyMode] = useState('native');

  useEffect(() => {
    if (!open || !estimateId) return;
    setData(null); setTrend(null); setError(null);
    axios.get(`${API_URL}/api/estimates/${estimateId}/deal-summary`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || 'Could not load deal summary'));
    axios.get(`${API_URL}/api/estimates/${estimateId}/margin-trend`, { withCredentials: true })
      .then((r) => setTrend(r.data)).catch(() => setTrend({ points: [] }));
  }, [open, estimateId]);

  const exportDoc = async (fmt) => {
    setExporting(fmt);
    try {
      const url = fmt === 'docx' ? `${API_URL}/api/estimates/${estimateId}/deal-summary/docx?ccy=${ccyMode}` : `${API_URL}/api/estimates/${estimateId}/deal-summary/pdf`;
      const r = await axios.get(url, { withCredentials: true, responseType: 'blob' });
      saveAs(r.data, `Deal_Summary_${(data?.estimate_name || 'estimate').replace(/[^\w\- ]+/g, '_').slice(0, 60)}.${fmt}`);
    } catch { toast.error(`Could not export deal summary ${fmt === 'docx' ? 'Word document' : 'PDF'}`); } finally { setExporting(false); }
  };
  const trendPts = [...(trend?.points || []), ...(trend?.current?.margin_pct != null ? [trend.current] : [])].filter((p) => p.margin_pct != null);

  const t = data?.totals;
  const aud = ccyMode === 'aud';
  const nativeCcy = data?.currency_code || 'AUD';
  const sym = aud ? 'A$' : getCurrencySymbol(nativeCcy);
  const fx = aud ? Number(data?.fx_rate_to_aud || 1) : 1;
  const pick = (local, inAud) => (aud ? inAud : local);
  const blended = t?.effort_hours ? pick(t.price, t.price_aud) / t.effort_hours : null;
  const marginTone = t?.margin_pct == null ? 'text-slate-500' : t.below_guardrail ? 'text-rose-600' : t.margin_pct < 30 ? 'text-amber-600' : 'text-emerald-600';

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-[960px] max-h-[90vh] overflow-y-auto" data-testid="deal-summary-dialog">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3 pr-6">
            <DialogTitle className="text-[#0F2040]">{data?.estimate_name || 'Deal summary'}</DialogTitle>
            {data && (
              <div className="flex items-center gap-1.5 shrink-0">
                <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-[11px]" role="group" aria-label="Currency" data-testid="deal-summary-ccy-toggle">
                  <button type="button" onClick={() => setCcyMode('native')} className={`px-2 py-1 ${!aud ? 'bg-[#0F2040] text-white' : 'text-slate-600 hover:bg-slate-50'}`} title={`Implementation in ${nativeCcy}${data.product ? `, subscription in ${data.product.currency || 'AUD'}` : ''}`} data-testid="deal-summary-ccy-native">Deal currency{nativeCcy !== 'AUD' || (data.product && data.product.currency !== 'AUD') ? ` (${[nativeCcy, data.product?.currency].filter((c, i, a) => c && a.indexOf(c) === i).join(' / ')})` : ''}</button>
                  <button type="button" onClick={() => setCcyMode('aud')} className={`px-2 py-1 border-l border-slate-200 ${aud ? 'bg-[#0F2040] text-white' : 'text-slate-600 hover:bg-slate-50'}`} title="Company currency at the saved FX rate" data-testid="deal-summary-ccy-aud">AUD</button>
                </div>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => exportDoc('pdf')} disabled={!!exporting} data-testid="deal-summary-export-pdf-btn">
                  {exporting === 'pdf' ? <Loader2 className="w-3 h-3 animate-spin" /> : <><FileDown className="w-3 h-3 mr-1" /> PDF</>}
                </Button>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => exportDoc('docx')} disabled={!!exporting} data-testid="deal-summary-export-docx-btn">
                  {exporting === 'docx' ? <Loader2 className="w-3 h-3 animate-spin" /> : <><FileText className="w-3 h-3 mr-1" /> Word</>}
                </Button>
              </div>
            )}
          </div>
          <DialogDescription>
            {data ? `${data.organization_name || '—'} · ${String(data.status).replace(/_/g, ' ')} · ${aud ? 'company currency (AUD)' : `priced in ${nativeCcy}`}${aud && nativeCcy !== 'AUD' ? ` · FX ${Number(data.fx_rate_to_aud).toFixed(4)}` : ''}` : 'Effort vs price vs margin at a glance'}
          </DialogDescription>
        </DialogHeader>

        {error && <p className="text-sm text-rose-600" data-testid="deal-summary-error">{error}</p>}
        {!data && !error && <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>}

        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="deal-summary-kpis">
              <Kpi icon={Clock} label="Implementation effort" value={hrs(t.effort_hours)} sub={`${data.factors?.length ? 'incl. project factors' : ''}`} testId="deal-kpi-effort" />
              <Kpi icon={Gauge} label="Impl. blended rate" value={blended == null ? '—' : `${sym}${Math.round(blended).toLocaleString()}/h`} sub={`sell price ÷ hours${aud && nativeCcy !== 'AUD' ? ` · ${getCurrencySymbol(nativeCcy)}${Math.round(t.blended_rate).toLocaleString()}/h native` : ''}`} testId="deal-kpi-blended" />
              <Kpi icon={Wallet} label="Resource cost" value={money(pick(t.cost, t.cost_aud), sym)} sub={!aud && t.cost_aud != null && nativeCcy !== 'AUD' ? `A$${Math.round(t.cost_aud).toLocaleString()}` : (data.cost_configured ? 'Internal cost rates' : 'No cost rates set')} tone="text-slate-700" testId="deal-kpi-cost" />
              <Kpi icon={Tag} label="Price" value={money(pick(t.price, t.price_aud), sym)} sub={!aud && nativeCcy !== 'AUD' ? `A$${Math.round(t.price_aud).toLocaleString()}` : 'Rate card sell price'} testId="deal-kpi-price" />
              <Kpi icon={Percent} label="Margin" value={t.margin_pct == null ? '—' : `${t.margin_pct}%`} sub={money(pick(t.margin, t.margin_aud), sym)} tone={marginTone} testId="deal-kpi-margin" />
            </div>

            {data.leadership_note && (
              <p className="flex items-start gap-1.5 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid="deal-summary-leadership-note">
                <StickyNote className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-600" />
                <span><b>Leadership note:</b> <i>“{data.leadership_note.text}”</i> <span className="text-amber-700/70">— {data.leadership_note.by}{data.leadership_note.at ? ` · ${new Date(data.leadership_note.at).toLocaleDateString()}` : ''}</span></span>
              </p>
            )}
            {t.below_guardrail && (
              <p className="flex items-center gap-1.5 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1.5" data-testid="deal-summary-guardrail">
                <AlertTriangle className="w-3.5 h-3.5" /> Margin is below the {data.min_margin_pct}% guardrail.
              </p>
            )}
            {data.benchmark_flags?.length > 0 && (
              <p className="flex items-start gap-1.5 text-xs text-violet-900 bg-violet-50 border border-violet-200 rounded px-2 py-1.5" data-testid="deal-summary-benchmark-flag">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-violet-600" />
                <span>
                  <b>{data.quoted ? 'Quoted with' : 'Review before quoting:'}</b> {data.benchmark_flags.map((f) => `${f.label} ×${f.multiplier.toFixed(2)} (portfolio avg ×${f.portfolio_avg_multiplier.toFixed(2)})`).join(', ')}
                  {' '}— well above the typical project factor across open deals.
                </span>
              </p>
            )}
            <BenchmarkTrendPanel estimateId={estimateId} enabled={data.benchmark_flags?.length > 0} />
            <ProductSubscriptionBlock product={data.product} estimateId={estimateId} mode={ccyMode} />
            {!data.cost_configured && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid="deal-summary-no-cost">
                Internal cost rates are not configured for this region — cost and margin columns are unavailable (Admin → Settings → Cost rate card).
              </p>
            )}
            {data.roles_missing_cost_rate?.length > 0 && (
              <p className="text-[11px] text-amber-700" data-testid="deal-summary-missing-roles">Roles without a cost rate (excluded from cost): {data.roles_missing_cost_rate.join(', ')}</p>
            )}

            <CommercialAnalysisBlock ca={data.commercial} />

            {trend && (
              <div data-testid="deal-margin-trend">
                <div className="flex items-center gap-2 mb-1.5">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Margin trend across versions</p>
                  {trend.erosion_pts != null && (
                    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold rounded-full px-2 py-0.5 border ${
                      trend.erosion_pts < -0.05 ? 'bg-rose-50 text-rose-700 border-rose-200' : trend.erosion_pts > 0.05 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-600 border-slate-200'}`}
                      data-testid="deal-margin-erosion">
                      {trend.erosion_pts < -0.05 ? <TrendingDown className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />}
                      {trend.erosion_pts > 0 ? '+' : ''}{trend.erosion_pts} pts since v{trend.points?.[0]?.version ?? 1}
                    </span>
                  )}
                  {trend.from_peak_pts != null && trend.from_peak_pts < -0.05 && (
                    <span className="text-[10px] text-slate-500" data-testid="deal-margin-from-peak">{trend.from_peak_pts} pts from peak</span>
                  )}
                </div>
                {trendPts.length === 0 ? (
                  <p className="text-[11px] text-slate-400" data-testid="deal-margin-trend-empty">No versions with cost data yet.</p>
                ) : (
                  <div className="flex items-end gap-1.5 h-20 px-1 border-b border-slate-200" data-testid="deal-margin-trend-bars">
                    {trendPts.map((p, i) => {
                      const below = trend.min_margin_pct != null && p.margin_pct < trend.min_margin_pct;
                      return (
                        <div key={i} className="flex-1 flex flex-col items-center justify-end h-full min-w-0" title={`v${p.version} · ${p.margin_pct}% margin · A$${Math.round(p.price_aud).toLocaleString()} price · ${p.created_at ? new Date(p.created_at).toLocaleDateString() : ''}${p.change_summary ? ` · ${p.change_summary}` : ''}`} data-testid={`deal-margin-point-${p.version}`}>
                          <span className={`text-[9px] tabular-nums mb-0.5 ${below ? 'text-rose-600 font-semibold' : 'text-slate-500'}`}>{p.margin_pct}%</span>
                          <div className={`w-full max-w-[36px] rounded-t ${p.version === 'current' ? 'bg-[#0F2040]' : below ? 'bg-rose-400' : 'bg-[#4ABFCF]'}`} style={{ height: `${Math.max(4, Math.min(100, p.margin_pct))}%` }} />
                          <span className="text-[9px] text-slate-400 mt-0.5 truncate max-w-full">{p.version === 'current' ? 'now' : `v${p.version}`}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">Price &amp; margin by project factor <span className="font-normal normal-case">· composite ×{t.composite_modifier} · {aud ? 'AUD' : nativeCcy}</span></p>
              <div className="rounded-lg border border-slate-200 overflow-hidden">
                <table className="w-full text-xs" data-testid="deal-summary-factors-table">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold">Project factor</th>
                      <th className="text-right px-2 py-2 font-semibold">×</th>
                      <th className="text-right px-2 py-2 font-semibold" title={`Portfolio average multiplier across ${data.benchmark_open_deals ?? '—'} open deals`}>vs portfolio</th>
                      <th className="text-right px-2 py-2 font-semibold">Effort</th>
                      <th className="text-right px-2 py-2 font-semibold">Price</th>
                      <th className="text-right px-2 py-2 font-semibold">Cost</th>
                      <th className="text-right px-2 py-2 font-semibold">Margin</th>
                      <th className="text-right px-3 py-2 font-semibold">Margin %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.factors.map((f) => {
                      const neutral = f.key !== 'baseline' && Math.abs(f.price) < 0.5;
                      return (
                        <tr key={f.key} className={`border-t border-slate-100 ${f.key === 'baseline' ? 'bg-slate-50/60 font-semibold' : neutral ? 'text-slate-400' : ''}`} data-testid={`deal-factor-${f.key}`}>
                          <td className="px-3 py-1.5">{f.label}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{f.multiplier == null ? '—' : f.multiplier.toFixed(2)}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums text-[11px]" data-testid={`deal-factor-bench-${f.key}`}>
                            {f.portfolio_avg_multiplier == null ? '—' : (
                              <span title={`Portfolio average ×${f.portfolio_avg_multiplier.toFixed(2)}`} className={Math.abs(f.vs_portfolio) < 0.005 ? 'text-slate-400' : f.vs_portfolio > 0 ? 'text-rose-600 font-semibold' : 'text-emerald-700 font-semibold'}>
                                {Math.abs(f.vs_portfolio) < 0.005 ? '= avg' : `${f.vs_portfolio > 0 ? '+' : '−'}${Math.abs(f.vs_portfolio).toFixed(2)}`}
                                <span className="text-slate-400 font-normal"> (avg {f.portfolio_avg_multiplier.toFixed(2)})</span>
                              </span>
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{f.key === 'baseline' || !neutral ? hrs(f.effort_hours) : '—'}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{neutral ? '—' : `${f.price < 0 ? '−' : ''}${money(Math.abs(f.price) * fx, sym)}`}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{f.cost == null || neutral ? '—' : `${f.cost < 0 ? '−' : ''}${money(Math.abs(f.cost) * fx, sym)}`}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{f.margin == null || neutral ? '—' : `${f.margin < 0 ? '−' : ''}${money(Math.abs(f.margin) * fx, sym)}`}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{f.margin_pct == null || neutral ? '—' : `${f.margin_pct}%`}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-[10px] text-slate-400 mt-1">Factor rows show the uplift each project factor adds on top of the baseline scope (attributed by multiplier weight). Neutral factors (×1.00) add nothing. “vs portfolio” compares this deal's multiplier with the average across {data.benchmark_open_deals ?? '—'} open deals.</p>
            </div>

            <div className="pt-3 border-t border-slate-100" data-testid="deal-summary-role-margin">
              <RoleMarginMatrix estimateId={estimateId} compact onApplied={() => {
                axios.get(`${API_URL}/api/estimates/${estimateId}/deal-summary`, { withCredentials: true }).then((r) => setData(r.data)).catch(() => {});
                axios.get(`${API_URL}/api/estimates/${estimateId}/margin-trend`, { withCredentials: true }).then((r) => setTrend(r.data)).catch(() => {});
              }} />
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
